import React, { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import { 
  Phone, 
  Mail, 
  Send,
  Clock,
  CheckCircle,
  MessageCircle,
  Smartphone
} from 'lucide-react';

const Contact = () => {
  const form = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const sendEmail = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    // Your EmailJS credentials
    // Sign up at https://www.emailjs.com to get these
    const serviceId = 'service_crxsvz6'; // Your EmailJS service ID
    const templateId = 'template_mvh50ps'; // Replace with your template ID
    const publicKey = 'fX23B9SX888oR9vXY'; // Your public key

    emailjs.sendForm(serviceId, templateId, form.current, {
      publicKey: publicKey,
    })
    .then(
      (result) => {
        console.log('SUCCESS!', result.text);
        setIsSubmitting(false);
        setIsSubmitted(true);
        form.current.reset(); // Reset the form
        
        // Reset success message after 5 seconds
        setTimeout(() => setIsSubmitted(false), 5000);
      },
      (error) => {
        console.log('FAILED...', error.text);
        setIsSubmitting(false);
        setSubmitError('Failed to send message. Please try again or contact us directly.');
      }
    );
  };

  const contactInfo = [
    { icon: Phone, label: 'Phone', value: '( +27 ) 71 966 9546 / ( +27 ) 71 631 0865', gradient: 'from-logo-lightblue to-logo-teal' },
    { icon: Mail, label: 'Email', value: 'info@adaptteck.co.za', gradient: 'from-logo-teal to-logo-green3' },
  ];

  const officeHours = [
    { day: 'Monday - Friday', hours: '08h00 - 17h00' },
    { day: 'Saturday', hours: '09h00 - 14h00' },
    { day: 'Sunday', hours: 'Closed' },
  ];

  // Scroll to contact section function
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="tech-pattern"></div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-logo-darkblue/10 
                        to-logo-lightblue/10 px-4 py-2 rounded-full mb-4">
            <MessageCircle className="text-logo-darkblue" size={16} />
            <span className="text-sm font-medium text-logo-navy">Get In Touch</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-bold text-logo-navy mb-4">
            Let's Start a{' '}
            <span className="bg-gradient-to-r from-logo-darkblue to-logo-lightblue bg-clip-text text-transparent">
              Conversation
            </span>
          </h2>
          
          <p className="text-gray-600 max-w-3xl mx-auto text-base sm:text-lg">
            Ready to transform your technology strategy? Contact us for a complimentary 
            consultation with our vCTO.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column - Contact Information */}
          <div className="space-y-8">
            {/* Contact Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                return (
                  <div
                    key={index}
                    className="group bg-white rounded-xl p-4 border border-gray-100 
                             hover:shadow-xl transition-all duration-300 hover:-translate-y-1
                             cursor-default"
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${info.gradient} 
                                     flex items-center justify-center flex-shrink-0
                                     group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="text-white" size={20} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 mb-1">{info.label}</p>
                        <p className="text-sm font-medium text-logo-navy">{info.value}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Office Hours */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-lg">
              <div className="flex items-center space-x-3 mb-4">
                <Clock className="text-logo-darkblue" size={24} />
                <h3 className="text-lg font-bold text-logo-navy">Office Hours</h3>
              </div>
              <div className="space-y-2">
                {officeHours.map((schedule, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">{schedule.day}</span>
                    <span className="font-medium text-logo-navy">{schedule.hours}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Response Guarantee */}
            <div className="bg-gradient-to-r from-logo-darkblue/5 to-logo-lightblue/5 
                          rounded-2xl p-6 border border-logo-lightblue/20">
              <div className="flex items-center space-x-3 mb-3">
                <CheckCircle className="text-logo-green" size={24} />
                <h3 className="text-lg font-bold text-logo-navy">Quick Response Guarantee</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                We respond to all inquiries within 24 hours. Most clients hear back within 2-4 hours during business days.
              </p>
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div>
            <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
              <h3 className="text-xl font-bold text-logo-navy mb-6">Send us a Message</h3>

              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-gradient-to-r from-logo-green to-logo-green2 
                                rounded-full flex items-center justify-center mx-auto mb-6
                                animate-bounce">
                    <CheckCircle className="text-white" size={40} />
                  </div>
                  <h4 className="text-xl font-bold text-logo-navy mb-3">Message Sent!</h4>
                  <p className="text-gray-600 text-base">
                    Thank you for reaching out. We'll get back to you within 24 hours.
                  </p>
                </div>
              ) : (
                <form ref={form} onSubmit={sendEmail} className="space-y-6">
                  {/* Error Message */}
                  {submitError && (
                    <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                      {submitError}
                    </div>
                  )}

                  {/* First row: Full Name and Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-logo-navy mb-2" htmlFor="user_name">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="user_name"
                        name="user_name"  // Important: name attribute must match EmailJS template
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg
                                 focus:outline-none focus:ring-2 focus:ring-logo-lightblue 
                                 focus:border-transparent transition-all duration-300
                                 hover:border-logo-lightblue"
                        placeholder="John Doe"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-logo-navy mb-2" htmlFor="user_email">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="user_email"
                        name="user_email"  // Important: name attribute must match EmailJS template
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg
                                 focus:outline-none focus:ring-2 focus:ring-logo-lightblue 
                                 focus:border-transparent transition-all duration-300
                                 hover:border-logo-lightblue"
                        placeholder="john@company.com"
                        required
                      />
                    </div>
                  </div>

                  {/* Second row: Phone and Mobile */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-logo-navy mb-2" htmlFor="user_phone">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="user_phone"
                        name="user_phone"  // Important: name attribute must match EmailJS template
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg
                                 focus:outline-none focus:ring-2 focus:ring-logo-lightblue 
                                 focus:border-transparent transition-all duration-300
                                 hover:border-logo-lightblue"
                        placeholder="+27 71 966 9546"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-logo-navy mb-2" htmlFor="user_mobile">
                        Mobile Number <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Smartphone className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="tel"
                          id="user_mobile"
                          name="user_mobile"  // Important: name attribute must match EmailJS template
                          className="w-full pl-10 px-4 py-3 text-sm border border-gray-200 rounded-lg
                                   focus:outline-none focus:ring-2 focus:ring-logo-lightblue 
                                   focus:border-transparent transition-all duration-300
                                   hover:border-logo-lightblue"
                          placeholder="+27 71 631 0865"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Third row: Company */}
                  <div>
                    <label className="block text-sm font-medium text-logo-navy mb-2" htmlFor="user_company">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="user_company"
                      name="user_company"  // Important: name attribute must match EmailJS template
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg
                               focus:outline-none focus:ring-2 focus:ring-logo-lightblue 
                               focus:border-transparent transition-all duration-300
                               hover:border-logo-lightblue"
                      placeholder="Your Company Name"
                      required
                    />
                  </div>

                  {/* Fourth row: Industry and Service */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-logo-navy mb-2" htmlFor="user_industry">
                        Industry <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="user_industry"
                        name="user_industry"  // Important: name attribute must match EmailJS template
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg
                                 focus:outline-none focus:ring-2 focus:ring-logo-lightblue 
                                 focus:border-transparent transition-all duration-300
                                 hover:border-logo-lightblue"
                        required
                      >
                        <option value="">Select Industry</option>
                        <option value="Education">Education</option>
                        <option value="Manufacturing">Manufacturing & Industrial</option>
                        <option value="Finance">Finance & Professional Services</option>
                        <option value="Telecommunications">Telecommunications</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Insurance">Long-Term Insurance</option>
                        <option value="Hospitality">Hospitality & Consumer Markets</option>
                        <option value="Security">Security & Services</option>
                        <option value="Public">Public Sector</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-logo-navy mb-2" htmlFor="user_service">
                        Service Interested In <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="user_service"
                        name="user_service"  // Important: name attribute must match EmailJS template
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg
                                 focus:outline-none focus:ring-2 focus:ring-logo-lightblue 
                                 focus:border-transparent transition-all duration-300
                                 hover:border-logo-lightblue"
                        required
                      >
                        <option value="">Select Service</option>
                        <option value="vCTO">vCTO & IT Strategy</option>
                        <option value="Cybersecurity">Cybersecurity & Compliance</option>
                        <option value="Cloud">Cloud & Infrastructure</option>
                        <option value="Mobile">Web & Mobile Applications</option>
                        <option value="CCTV">CCTV & Physical Security</option>
                        <option value="Support">IT Support & Managed Services</option>
                      </select>
                    </div>
                  </div>

                  {/* Fifth row: Message */}
                  <div>
                    <label className="block text-sm font-medium text-logo-navy mb-2" htmlFor="message">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"  // Important: name attribute must match EmailJS template
                      rows="5"
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg
                               focus:outline-none focus:ring-2 focus:ring-logo-lightblue 
                               focus:border-transparent transition-all duration-300
                               hover:border-logo-lightblue resize-none"
                      placeholder="Tell us about your project or requirements..."
                      required
                    ></textarea>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary group flex items-center justify-center space-x-2
                             disabled:opacity-50 disabled:cursor-not-allowed text-base py-3"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent 
                                      rounded-full animate-spin"></div>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <span>Send Message</span>
                        <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>

                  {/* Required fields note */}
                  <p className="text-xs text-gray-400 text-center mt-2">
                    <span className="text-red-500">*</span> Required fields
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;